import { isHoliday } from '../data/hkHolidays';
import { tunnelTimeRangeFees } from '../data/tunnelTimeRangeFees';

const DAY_SECONDS = 24 * 60 * 60;

function parseTimeToSeconds(time) {
  const [hours, minutes, seconds = 0] = time.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}

function getHongKongTimeParts(dateTime) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Hong_Kong',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(dateTime);

  return {
    hours: Number(parts.find(part => part.type === 'hour').value),
    minutes: Number(parts.find(part => part.type === 'minute').value),
    seconds: Number(parts.find(part => part.type === 'second').value),
  };
}

function getHongKongDateParts(dateTime) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(dateTime);

  return {
    year: Number(parts.find(part => part.type === 'year').value),
    month: Number(parts.find(part => part.type === 'month').value),
    day: Number(parts.find(part => part.type === 'day').value),
  };
}

function getHongKongDateAtOffset(dateTime, dayOffset) {
  const { year, month, day } = getHongKongDateParts(dateTime);
  return new Date(Date.UTC(year, month - 1, day + dayOffset, 4, 0, 0));
}

function interpolateFee(rangeFee, totalSeconds, startTotalSeconds, endTotalSeconds) {
  const minutesPassed = Math.floor((totalSeconds - startTotalSeconds) / 60);
  const feeChangeInterval = Math.floor(minutesPassed / 2);
  const feeChangeIntervals = Math.floor((endTotalSeconds - startTotalSeconds) / 120);
  const feeChangeStep = Math.abs(rangeFee.end - rangeFee.start) / feeChangeIntervals;
  const feeChange = feeChangeInterval * feeChangeStep * (rangeFee.end > rangeFee.start ? 1 : -1);
  return rangeFee.start + feeChange;
}

function resolveFee(range, vehicle, totalSeconds) {
  const startTotalSeconds = parseTimeToSeconds(range.start);
  const endTotalSeconds = parseTimeToSeconds(range.end);

  if (typeof range.fee === 'number') {
    return range.fee;
  }

  if (range.fee?.[vehicle] !== undefined) {
    const vehicleFee = range.fee[vehicle];
    return typeof vehicleFee === 'number'
      ? vehicleFee
      : interpolateFee(vehicleFee, totalSeconds, startTotalSeconds, endTotalSeconds);
  }

  return interpolateFee(range.fee, totalSeconds, startTotalSeconds, endTotalSeconds);
}

function getSlot(range) {
  if (range.start === '00:00' && range.end === '23:59:59') {
    return 'fixed';
  }

  if (range.slot) return range.slot;

  return 'normal';
}

function getNextRange(ranges, currentIndex) {
  return ranges[(currentIndex + 1) % ranges.length];
}

function getDayType(dateTime) {
  return isHoliday(dateTime) ? 'holiday' : 'weekday';
}

function getComparableFee(range, vehicle, totalSeconds) {
  const fee = resolveFee(range, vehicle, totalSeconds);
  return Math.round(fee * 10) / 10;
}

function isMeaningfulNextStage(currentRange, nextRange, vehicle, nextStartSeconds, currentFee) {
  return getComparableFee(nextRange, vehicle, nextStartSeconds) !== currentFee;
}

function findNextMeaningfulStage({ date, tunnelData, currentRange, currentIndex, vehicle, totalSeconds, currentFee }) {
  for (let dayOffset = 0; dayOffset <= 7; dayOffset += 1) {
    const dayDate = getHongKongDateAtOffset(date, dayOffset);
    const dayRanges = tunnelData[getDayType(dayDate)];
    const startIndex = dayOffset === 0 ? currentIndex + 1 : 0;

    for (let rangeIndex = startIndex; rangeIndex < dayRanges.length; rangeIndex += 1) {
      const candidateRange = dayRanges[rangeIndex];
      const candidateStartSeconds = parseTimeToSeconds(candidateRange.start);

      if (isMeaningfulNextStage(currentRange, candidateRange, vehicle, candidateStartSeconds, currentFee)) {
        return {
          range: candidateRange,
          secondsToNext: (dayOffset * DAY_SECONDS) + candidateStartSeconds - totalSeconds,
          startSeconds: candidateStartSeconds,
        };
      }
    }
  }

  const nextRange = getNextRange(tunnelData[getDayType(date)], currentIndex);
  const nextStartSeconds = parseTimeToSeconds(nextRange.start);

  return {
    range: nextRange,
    secondsToNext: parseTimeToSeconds(currentRange.end) - totalSeconds + 1,
    startSeconds: nextStartSeconds,
  };
}

function formatDuration(seconds) {
  const clampedSeconds = Math.max(0, seconds);
  const hours = Math.floor(clampedSeconds / 3600);
  const minutes = Math.floor((clampedSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${Math.max(1, minutes)}m`;
}

function getTimelineSegments(ranges) {
  return [-100, 0, 100].flatMap(dayOffset => (
    ranges.map(range => {
      const startSeconds = parseTimeToSeconds(range.start);
      const endSeconds = parseTimeToSeconds(range.end) + 1;

      return {
        slot: getSlot(range),
        left: dayOffset + (startSeconds / DAY_SECONDS) * 100,
        width: ((endSeconds - startSeconds) / DAY_SECONDS) * 100,
      };
    })
  ));
}

export function getTunnelStageStatus(dateTime, tunnelAbbr, vehicle) {
  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
  const { hours, minutes, seconds } = getHongKongTimeParts(date);
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;
  const dayType = getDayType(date);
  const tunnelData = tunnelTimeRangeFees.find(tunnel => tunnel.abbr === tunnelAbbr);

  if (!tunnelData) return null;

  const ranges = tunnelData[dayType];
  const currentIndex = ranges.findIndex(range => {
    const startTotalSeconds = parseTimeToSeconds(range.start);
    const endTotalSeconds = parseTimeToSeconds(range.end);
    return totalSeconds >= startTotalSeconds && totalSeconds <= endTotalSeconds;
  });

  if (currentIndex === -1) return null;

  const currentRange = ranges[currentIndex];
  const startTotalSeconds = parseTimeToSeconds(currentRange.start);
  const endTotalSeconds = parseTimeToSeconds(currentRange.end);
  const durationSeconds = endTotalSeconds - startTotalSeconds + 1;
  const progress = Math.min(1, Math.max(0, (totalSeconds - startTotalSeconds) / durationSeconds));
  const currentFee = getComparableFee(currentRange, vehicle, totalSeconds);
  const slot = getSlot(currentRange);
  const nextStage = slot === 'fixed'
    ? {
      range: getNextRange(ranges, currentIndex),
      secondsToNext: endTotalSeconds - totalSeconds + 1,
      startSeconds: parseTimeToSeconds(getNextRange(ranges, currentIndex).start),
    }
    : findNextMeaningfulStage({
      date,
      tunnelData,
      currentRange,
      currentIndex,
      vehicle,
      totalSeconds,
      currentFee,
    });
  const nextRange = nextStage.range;
  const nextFee = getComparableFee(nextRange, vehicle, nextStage.startSeconds);
  const nextSlot = getSlot(nextRange);
  const trend = nextFee > currentFee ? 'up' : nextFee < currentFee ? 'down' : 'same';

  return {
    slot,
    nextSlot,
    trend,
    currentFee: Math.round(currentFee * 10) / 10,
    nextFee: Math.round(nextFee * 10) / 10,
    progress,
    dayProgress: totalSeconds / DAY_SECONDS,
    timelineSegments: getTimelineSegments(ranges),
    durationText: formatDuration(nextStage.secondsToNext),
  };
}
